import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentBox } from "@msuite/ui/content-box";
import { icon } from "@msuite/ui/icons";
import { LoadingScreen } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { Tabs } from "@msuite/ui/tabs";
import { ResponseError } from "@msuite/utils/errors";
import { useParams } from "react-router";
import { useSuborder } from "@/hooks/use-suborder";
import { SuborderArticles } from "./panels/suborder-articles";
import { SuborderGeneral } from "./panels/suborder-general";
import { SuborderLoadCarriers } from "./panels/suborder-load-carriers";
import { SuborderProtocol } from "./panels/suborder-protocol";
import { SuborderTransport } from "./panels/suborder-transport";
import { SuborderHeader } from "./suborder-header";

/**
 * One suborder: the slice of an order that lives in a single warehouse zone, and the work a picker
 * does there — the article lines off its shelves, and the load carriers the goods travel in.
 *
 * `read Stock` covers every panel, so a refusal is the whole page — the same shape the order page
 * takes one level up, including the split between being kept out and there being nothing there.
 */
export function Suborder() {
	/** Hooks */
	const { t } = useTranslation();
	const params = useParams<{ orderId: string; suborderId: string }>();
	const orderId = params.orderId as string;
	const suborderId = params.suborderId as string;

	/** Query */
	const { suborder, isLoading, isError, error } = useSuborder(orderId, suborderId);

	/** Computed */
	const isForbidden = error instanceof ResponseError && error.status === 403;

	/** Guard */
	if (isForbidden) {
		return (
			<ContentBox>
				<BlankSlate
					Icon={icon("Lock")}
					label={t("general.no_access")}
					withAccessory={false}
				/>
			</ContentBox>
		);
	}
	if (isError) {
		return (
			<ContentBox>
				<BlankSlate label={t("stock.suborder_not_loaded")} />
			</ContentBox>
		);
	}
	if (isLoading) return <LoadingScreen />;
	if (!suborder) {
		return (
			<ContentBox>
				<BlankSlate label={t("stock.suborder_not_loaded")} />
			</ContentBox>
		);
	}

	/** Render */
	return (
		<ContentBox bottomPadding="none">
			<SuborderHeader
				orderId={orderId}
				suborderId={suborderId}
			/>
			<Tabs
				groups={[
					{
						name: "general",
						panels: [
							{
								label: t("general.general"),
								cyId: "suborder-general-tab",
								panel: () => (
									<SuborderGeneral
										orderId={orderId}
										suborderId={suborderId}
									/>
								),
								errors: [],
							},
						],
					},
					{
						name: "information",
						panels: [
							{
								label: t("stock.articles"),
								cyId: "suborder-articles-tab",
								panel: () => (
									<SuborderArticles
										orderId={orderId}
										suborderId={suborderId}
									/>
								),
								errors: [],
							},
							{
								label: t("stock.load_carriers"),
								cyId: "suborder-load-carriers-tab",
								panel: () => (
									<SuborderLoadCarriers
										orderId={orderId}
										suborderId={suborderId}
									/>
								),
								errors: [],
							},
							{
								label: t("stock.protocol"),
								cyId: "suborder-protocol-tab",
								panel: () => (
									<SuborderProtocol
										orderId={orderId}
										suborderId={suborderId}
									/>
								),
								errors: [],
							},
							{
								label: t("stock.transport_events"),
								cyId: "suborder-transport-tab",
								panel: () => (
									<SuborderTransport
										orderId={orderId}
										suborderId={suborderId}
									/>
								),
								errors: [],
							},
						],
					},
				]}
			/>
		</ContentBox>
	);
}
